/**
 * Map marker art from the ShiftWagon rebrand asset drop.
 *
 * The paths are the ones in `assets/markers/*.svg`, inlined rather than fetched.
 * Each is a few hundred bytes, so a network round-trip per marker would cost
 * more than the art itself — and a marker that arrives late is a marker the
 * customer watches pop in. Inlining also makes colour and rotation parameters
 * instead of a file per variant, which is the whole reason the brief ships SVG
 * over the PNG fallback set.
 *
 * Sizes and anchors are fixed by the brief: pins anchor at the tip so the point
 * sits on the coordinate, the puck and the dot anchor centre.
 */

interface MarkerColours {
  /** Pickup pin fill, and the route line it terminates. */
  route: string;
  /** Drop-off and wagon fill. */
  asphalt: string;
  /** The outline every marker carries — the brief's contrast device. */
  paper: string;
  /** Wagon chevrons and the drop-off square. */
  signal: string;
}

/**
 * Google Maps takes bitmap-or-URL icons, so the SVG ships as a data URI.
 *
 * `encodeURIComponent` rather than base64: the payload stays readable in
 * devtools, and it is shorter for markup this small.
 */
function dataUri(svg: string): string {
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg.replace(/\s+/g, ' ').trim())}`;
}

/** Pickup — teal pin, paper dot. 40 × 52, anchored at the tip. */
export function pickupSvg({ route, paper }: MarkerColours): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">
    <path d="M20 50.5 L14 36 A10 10 0 0 1 4 26 V14 A10 10 0 0 1 14 4 H26 A10 10 0 0 1 36 14 V26 A10 10 0 0 1 26 36 Z"
      fill="${route}" stroke="${paper}" stroke-width="2.5" stroke-linejoin="round"/>
    <circle cx="20" cy="20" r="6" fill="${paper}"/>
  </svg>`;
}

/**
 * Drop-off — asphalt pin, signal-yellow square. 40 × 52, anchored at the tip.
 *
 * Asphalt and never red: the brief is explicit that red reads as a failed
 * delivery, which is the opposite of what a drop-off pin means.
 */
export function dropoffSvg({ asphalt, paper, signal }: MarkerColours): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="40" height="52" viewBox="0 0 40 52">
    <path d="M20 50.5 L14 36 A10 10 0 0 1 4 26 V14 A10 10 0 0 1 14 4 H26 A10 10 0 0 1 36 14 V26 A10 10 0 0 1 26 36 Z"
      fill="${asphalt}" stroke="${paper}" stroke-width="2.5" stroke-linejoin="round"/>
    <rect x="14" y="14" width="12" height="12" rx="2.5" fill="${signal}"/>
  </svg>`;
}

/**
 * Wagon — asphalt puck, twin signal chevrons. 48 × 48, anchored centre.
 *
 * The only marker that rotates. The whole puck turns by GPS bearing so the
 * chevrons point along the direction of travel; with no heading in the feed the
 * puck sits at 0° rather than guessing, which would point the customer's eye
 * down a road the driver is not on.
 */
export function vehicleSvg({ asphalt, paper, signal }: MarkerColours, bearing = 0): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 48 48">
    <circle cx="24" cy="24" r="18" fill="${asphalt}" stroke="${paper}" stroke-width="3"/>
    <g transform="rotate(${bearing.toFixed(1)} 24 24)">
      <path d="M16 24.5 L24 16.5 L32 24.5" fill="none" stroke="${signal}" stroke-width="4.5"
        stroke-linecap="square" stroke-linejoin="miter"/>
      <path d="M16 31.5 L24 23.5 L32 31.5" fill="none" stroke="${signal}" stroke-width="4.5"
        stroke-linecap="square" stroke-linejoin="miter"/>
    </g>
  </svg>`;
}

/** Waypoint dot — for stops and clusters. 24 × 24, anchored centre. */
export function dotSvg({ route, paper }: MarkerColours): string {
  return `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="9" fill="${route}" stroke="${paper}" stroke-width="3"/>
  </svg>`;
}

/**
 * Wrap art in the icon shape Google expects.
 *
 * `scaledSize` is passed even at 1× because without it Google draws the SVG at
 * its intrinsic size and anchors against that, which puts the pin tip off the
 * coordinate on any device with a non-integer pixel ratio.
 */
function icon(
  google: any,
  svg: string,
  width: number,
  height: number,
  anchorX: number,
  anchorY: number,
) {
  return {
    url: dataUri(svg),
    scaledSize: new google.maps.Size(width, height),
    anchor: new google.maps.Point(anchorX, anchorY),
  };
}

export function pickupIcon(google: any, colours: MarkerColours) {
  return icon(google, pickupSvg(colours), 40, 52, 20, 52);
}

export function dropoffIcon(google: any, colours: MarkerColours) {
  return icon(google, dropoffSvg(colours), 40, 52, 20, 52);
}

export function vehicleIcon(google: any, colours: MarkerColours, bearing = 0) {
  return icon(google, vehicleSvg(colours, bearing), 48, 48, 24, 24);
}

export function dotIcon(google: any, colours: MarkerColours) {
  return icon(google, dotSvg(colours), 24, 24, 12, 12);
}

export type { MarkerColours };
